/**
 * Consolidates duplicate table / photo log field instances into one logical instance per form + template.
 *
 * A form can end up with two FieldInstance documents for the same table or imageroll template (a repair step once
 * created a second, empty set of fields against a corrupted load, and rows were then added to both). The sync merge
 * matches instances by id only, so both copies survive every sync, the renderer has to pick one, and the export
 * picks whichever comes first. This helper is the single rule both the merge and the folder-open repair use:
 *  - the oldest instance (by createdAt, then smallest id) is the survivor
 *  - every loser row whose cells do not already appear in the survivor is appended to the survivor's fieldsOrder,
 *    using the same cell-id-overlap test the per-instance merge uses for rows added offline on two devices
 *  - every cell that points at a loser is re-pointed at the survivor (tables fetch their cells by parent id, so an
 *    adopted row whose cells still pointed at the loser would render blank)
 *  - the loser keeps everything except its rows: fieldsOrder becomes []. Nothing is ever deleted.
 * Timestamps are never touched. The per-instance merge can bring a loser's rows back from a stale device (its
 * fieldsOrder merge is a union), so the rule has to be idempotent and run on every merge; it is, because the
 * survivor depends only on immutable fields and resurrected rows always overlap the survivor's.
 *
 * Pure: no database access, no Sentry. Callers decide what to write and what to report.
 */

export interface ConsolidatableFieldInstance {
	id: string;
	createdAt?: string | null;
	formInstance?: string | null;
	fieldTemplateId?: string | null;
	tableField?: string | null;
	fieldsOrder?: string[] | null;
	authors?: string[] | null;
}

/**
 * Tells the helper whether a template is a table / imageroll. `undefined` means the template is unknown to the
 * caller; that group is left alone and counted, because emptying a non-table instance would be irreversible.
 */
export type IsTableTemplate = (fieldTemplateId: string) => boolean | undefined;

export interface TableConsolidation {
	formInstance: string;
	fieldTemplateId: string;
	survivorId: string;
	loserIds: string[];
	/** Rows the survivor had before any loser rows were adopted */
	rowsBefore: number;
	/** Loser rows appended to the survivor */
	rowsAdopted: number;
	/** Loser rows skipped because one of their cells already appears in the survivor */
	rowsSkippedOverlap: number;
	/** Adopted rows that reference a cell id not present in the input (still adopted; cells resolve by id later) */
	rowsWithMissingCells: number;
	/** Cells whose tableField was moved from a loser to the survivor */
	cellsRepointed: number;
}

export interface TableConsolidationResult<T> {
	/** Same order and length as the input. Changed documents are new objects; untouched ones are the same references. */
	fieldInstances: T[];
	/** One entry per group where something actually changed */
	consolidations: TableConsolidation[];
	/** Duplicate groups left alone because the caller could not say whether the template is a table */
	skippedUnknownTemplate: number;
}

function isOlder(a: ConsolidatableFieldInstance, b: ConsolidatableFieldInstance): number {
	const aCreated = a.createdAt || "";
	const bCreated = b.createdAt || "";
	if (aCreated !== bCreated) return aCreated < bCreated ? -1 : 1;
	if (a.id !== b.id) return a.id < b.id ? -1 : 1;
	return 0;
}

function rowCellIds(row: string): string[] {
	return row.split(",").filter((cellId) => cellId.length > 0);
}

function uniqueStrings(...lists: (string[] | null | undefined)[]): string[] {
	const seen = new Set<string>();
	const result: string[] = [];
	for (const list of lists) {
		for (const item of list ?? []) {
			if (!seen.has(item)) {
				seen.add(item);
				result.push(item);
			}
		}
	}
	return result;
}

export function consolidateDuplicateTableInstances<T extends ConsolidatableFieldInstance>(
	fieldInstances: T[],
	isTableTemplate: IsTableTemplate,
): TableConsolidationResult<T> {
	// Only top-level instances (they carry formInstance and no tableField) can be duplicates of each other. Cells
	// carry tableField; LINK alt instances carry neither. Both are only ever re-pointed, never grouped.
	const groups = new Map<string, T[]>();
	const cellsByParent = new Map<string, T[]>();
	const knownIds = new Set<string>();
	for (const fieldInstance of fieldInstances) {
		knownIds.add(fieldInstance.id);
		if (fieldInstance.tableField) {
			const siblings = cellsByParent.get(fieldInstance.tableField);
			if (siblings) siblings.push(fieldInstance);
			else cellsByParent.set(fieldInstance.tableField, [fieldInstance]);
			continue;
		}
		if (!fieldInstance.formInstance || !fieldInstance.fieldTemplateId) continue;
		const key = `${fieldInstance.formInstance}|${fieldInstance.fieldTemplateId}`;
		const group = groups.get(key);
		if (group) group.push(fieldInstance);
		else groups.set(key, [fieldInstance]);
	}

	const replacements = new Map<string, T>();
	const consolidations: TableConsolidation[] = [];
	let skippedUnknownTemplate = 0;

	groups.forEach((group) => {
		if (group.length < 2) return;
		const templateId = group[0].fieldTemplateId as string;
		const tableness = isTableTemplate(templateId);
		if (tableness === undefined) {
			skippedUnknownTemplate++;
			return;
		}
		if (!tableness) return;

		const ordered = [...group].sort(isOlder);
		const survivor = ordered[0];
		const losers = ordered.slice(1);

		const survivorRows = [...(survivor.fieldsOrder ?? [])];
		const survivorCellIds = new Set(survivorRows.flatMap(rowCellIds));
		const consolidation: TableConsolidation = {
			formInstance: survivor.formInstance as string,
			fieldTemplateId: templateId,
			survivorId: survivor.id,
			loserIds: losers.map((loser) => loser.id),
			rowsBefore: survivorRows.length,
			rowsAdopted: 0,
			rowsSkippedOverlap: 0,
			rowsWithMissingCells: 0,
			cellsRepointed: 0,
		};
		let loserHadRows = false;

		for (const loser of losers) {
			for (const row of loser.fieldsOrder ?? []) {
				loserHadRows = true;
				const cellIds = rowCellIds(row);
				if (cellIds.some((cellId) => survivorCellIds.has(cellId))) {
					consolidation.rowsSkippedOverlap++;
					continue;
				}
				// The row is adopted even when some of its cells are not in this array: cells are resolved by id when the
				// table renders or exports, and dropping the reference would be the only irreversible choice here.
				if (cellIds.some((cellId) => !knownIds.has(cellId))) consolidation.rowsWithMissingCells++;
				survivorRows.push(row);
				cellIds.forEach((cellId) => survivorCellIds.add(cellId));
				consolidation.rowsAdopted++;
			}
			for (const cell of cellsByParent.get(loser.id) ?? []) {
				replacements.set(cell.id, { ...cell, tableField: survivor.id });
				consolidation.cellsRepointed++;
			}
		}

		if (consolidation.rowsAdopted === 0 && consolidation.cellsRepointed === 0 && !loserHadRows) return;

		replacements.set(survivor.id, {
			...survivor,
			fieldsOrder: survivorRows,
			authors: uniqueStrings(survivor.authors, ...losers.map((loser) => loser.authors)),
		});
		for (const loser of losers) {
			if ((loser.fieldsOrder ?? []).length > 0) replacements.set(loser.id, { ...loser, fieldsOrder: [] });
		}
		consolidations.push(consolidation);
	});

	if (replacements.size === 0) return { fieldInstances, consolidations, skippedUnknownTemplate };
	return {
		fieldInstances: fieldInstances.map((fieldInstance) => replacements.get(fieldInstance.id) ?? fieldInstance),
		consolidations,
		skippedUnknownTemplate,
	};
}

export default consolidateDuplicateTableInstances;
